import { useCallback, useEffect, useState } from "react";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { coloringApi, ExportBlockedError } from "./api.js";
import { useColoring } from "./ColoringContext.js";
import { AutoField, SectionCard } from "./fields.js";
import type { ExportKind, ExportSummary, PrintSettings } from "./types.js";

const PAPER: { id: PrintSettings["paperType"]; label: string; thickness: number }[] = [
  { id: "white", label: "Black & white, white paper", thickness: 0.002252 },
  { id: "cream", label: "Black & white, cream paper", thickness: 0.0025 },
  { id: "color", label: "Color interior", thickness: 0.002347 }
];

const EXPORTS: { kind: ExportKind; title: string; desc: string; icon: IconName; needsCover?: boolean }[] = [
  { kind: "pdf", title: "Coloring Book PDF", desc: "High-resolution, print-ready book: cover, front matter, pages (with blank backs if single-sided) and back cover.", icon: "book" },
  { kind: "digital-pdf", title: "Digital Printable PDF", desc: "For customers who download and print pages themselves: one US Letter page per drawing, no blank backs.", icon: "file" },
  { kind: "kdp-interior", title: "KDP Interior PDF", desc: "Interior only, at your trim size with your bleed, margins and gutter, padded to an even page count.", icon: "layers" },
  { kind: "kdp-cover", title: "KDP Cover (wraparound)", desc: "Back + spine + front on one sheet, with the spine width worked out from your page count and paper.", icon: "image" },
  { kind: "zip", title: "ZIP of page images", desc: "Every coloring page as its own image file, in order, plus the cover artwork.", icon: "file" }
];

function NumberField({ label, value, step, min, max, suffix, onSave, hint }: { label: string; value: number; step: number; min: number; max: number; suffix?: string; onSave: (n: number) => void; hint?: string }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const commit = () => {
    const n = Number(text);
    if (!Number.isFinite(n)) return setText(String(value));
    const clamped = Math.min(max, Math.max(min, n));
    setText(String(clamped));
    if (clamped !== value) onSave(clamped);
  };
  return (
    <div className="field">
      <label className="label">{label}</label>
      <div className="row gap-2">
        <input className="input" type="number" step={step} min={min} max={max} value={text} onChange={(e) => setText(e.target.value)} onBlur={commit} />
        {suffix && <span className="faint" style={{ fontSize: 12 }}>{suffix}</span>}
      </div>
      {hint && <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Check({ label, checked, onChange, disabled, hint }: { label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; hint?: string }) {
  return (
    <div>
      <label className="pdf-toggle" style={disabled ? { opacity: 0.55 } : undefined}>
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span>{label}</span>
      </label>
      {hint && <div className="faint" style={{ fontSize: 11.5, margin: "-4px 0 6px 26px" }}>{hint}</div>}
    </div>
  );
}

/** Print Settings + every export, in one tab. */
export function ExportPanel() {
  const { projectId, state, saveBook } = useColoring();
  const toast = useToast();
  const { book, pages } = state;
  const p = book.print;
  const [summary, setSummary] = useState<ExportSummary | null>(null);
  const [busy, setBusy] = useState<ExportKind | null>(null);
  const [blocked, setBlocked] = useState<{ kind: ExportKind; message: string; missing: number[] } | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [showFiles, setShowFiles] = useState(false);

  const signature = pages.map((pg) => `${pg.id}:${pg.currentVariantId ?? ""}:${pg.status}`).join("|") + JSON.stringify(p) + `${book.trimWidth}x${book.trimHeight}`;
  useEffect(() => {
    let alive = true;
    coloringApi.exportSummary(projectId).then((s) => alive && setSummary(s)).catch(() => alive && setSummary(null));
    return () => {
      alive = false;
    };
  }, [projectId, signature]);

  const setPrint = (patch: Partial<PrintSettings>) => saveBook({ print: patch });

  const runExport = useCallback(
    async (kind: ExportKind, skipMissing = false) => {
      setBusy(kind);
      setBlocked(null);
      setNotes([]);
      try {
        const res = await coloringApi.downloadExport(projectId, kind, skipMissing);
        setNotes(res.notes);
        toast.success(`Exported ${res.filename}`);
      } catch (err) {
        if (err instanceof ExportBlockedError && err.status === 409) setBlocked({ kind, message: err.message, missing: err.missing });
        else toast.error(err instanceof Error ? err.message : "Export failed.");
      } finally {
        setBusy(null);
      }
    },
    [projectId, toast]
  );

  const withArt = pages.filter((pg) => pg.currentVariantId);

  return (
    <div className="coloring-scroll">
      <div className="coloring-grid">
        <SectionCard className="span-2" icon="download" title="Export">
          {blocked && (
            <div className="gen-note warn" style={{ alignItems: "center" }}>
              <Icon name="alert" size={14} />
              <span style={{ flex: 1 }}>{blocked.message}</span>
              <button className="btn btn-sm" onClick={() => void runExport(blocked.kind, true)}>Export without them</button>
            </div>
          )}
          {summary && summary.pages.total > 0 && summary.pages.missing.length > 0 && !blocked && (
            <p className="faint" style={{ fontSize: 12, marginTop: 0 }}>
              <Icon name="info" size={12} /> {summary.pages.missing.length} page{summary.pages.missing.length === 1 ? " has" : "s have"} no artwork yet and will block print exports until they're finished or removed.
            </p>
          )}
          <div className="export-cards">
            {EXPORTS.map((x) => (
              <div key={x.kind} className="export-card">
                <Icon name={x.icon} size={20} style={{ color: "var(--accent)" }} />
                <div className="grow">
                  <div className="strong">{x.title}</div>
                  <div className="faint" style={{ fontSize: 12 }}>{x.desc}</div>
                </div>
                <button className="btn btn-sm btn-primary" disabled={busy !== null || pages.length === 0} onClick={() => void runExport(x.kind)}>
                  <Icon name={busy === x.kind ? "clock" : "download"} size={14} className={busy === x.kind ? "spin" : ""} /> {busy === x.kind ? "Building…" : "Download"}
                </button>
              </div>
            ))}
            <div className="export-card">
              <Icon name="image" size={20} style={{ color: "var(--accent)" }} />
              <div className="grow">
                <div className="strong">Individual PNG files</div>
                <div className="faint" style={{ fontSize: 12 }}>Download any page's image on its own.</div>
              </div>
              <button className="btn btn-sm" disabled={withArt.length === 0} onClick={() => setShowFiles((s) => !s)}>{showFiles ? "Hide" : `Show ${withArt.length}`}</button>
            </div>
          </div>
          {showFiles && (
            <ul className="file-list">
              {pages.map((pg, i) =>
                pg.currentVariantId ? (
                  <li key={pg.id}>
                    <img src={coloringApi.imageUrl(projectId, pg.currentVariantId)} alt="" loading="lazy" />
                    <span className="truncate grow">Page {i + 1} — {pg.title || "Untitled"}</span>
                    <a className="btn btn-sm btn-ghost" href={coloringApi.pageDownloadUrl(projectId, pg.id)} download>Download</a>
                  </li>
                ) : null
              )}
            </ul>
          )}
          {notes.length > 0 && (
            <ul className="export-notes">
              {notes.map((n, i) => <li key={i}><Icon name="info" size={12} /> {n}</li>)}
            </ul>
          )}
        </SectionCard>

        <SectionCard icon="settings" title="Print settings">
          <div className="field">
            <label className="label">Sides</label>
            <div className="choice-row">
              <button className={`choice ${p.sided === "single" ? "on" : ""}`} onClick={() => void setPrint({ sided: "single" })}>Single-sided pages</button>
              <button className={`choice ${p.sided === "double" ? "on" : ""}`} onClick={() => void setPrint({ sided: "double" })}>Double-sided</button>
            </div>
          </div>
          <Check
            label="Add blank back pages"
            checked={p.sided === "single" && p.blankBacks}
            disabled={p.sided === "double"}
            onChange={(v) => void setPrint({ blankBacks: v })}
            hint={p.sided === "double" ? "Only applies to single-sided books." : "A blank page behind each drawing so markers don't bleed through."}
          />
          <div className="coloring-fields">
            <NumberField label="Margins" value={p.marginIn} step={0.05} min={0} max={2} suffix="in" onSave={(n) => void setPrint({ marginIn: n })} />
            <NumberField label="Gutter (inside margin)" value={p.gutterIn} step={0.0625} min={0} max={1.5} suffix="in" onSave={(n) => void setPrint({ gutterIn: n })} hint="Extra space on the binding side." />
          </div>
          <Check label="Bleed" checked={p.bleed} onChange={(v) => void setPrint({ bleed: v })} hint="Adds extra paper beyond the trim edge. Your artwork has white margins, so nothing gets cut either way." />
          {p.bleed && <NumberField label="Bleed amount" value={p.bleedIn} step={0.0625} min={0} max={0.5} suffix="in" onSave={(n) => void setPrint({ bleedIn: n })} />}
          <Check label="Page numbers" checked={p.pageNumbers} onChange={(v) => void setPrint({ pageNumbers: v })} />
        </SectionCard>

        <SectionCard icon="file" title="Include">
          <Check label="Cover and back cover (in the Coloring Book PDF)" checked={p.includeCover} onChange={(v) => void setPrint({ includeCover: v })} />
          <Check label="Title page" checked={p.titlePage} onChange={(v) => void setPrint({ titlePage: v })} />
          <Check label="Copyright page" checked={p.copyrightPage} onChange={(v) => void setPrint({ copyrightPage: v })} />
          {p.copyrightPage && <AutoField label="Copyright text" rows={2} value={p.copyrightText} onSave={(v) => setPrint({ copyrightText: v })} placeholder="Leave blank for a standard notice." />}
          <Check label={'"This Book Belongs To" page'} checked={p.belongsToPage} onChange={(v) => void setPrint({ belongsToPage: v })} />
          <Check label="Test coloring page" checked={p.testPage} onChange={(v) => void setPrint({ testPage: v })} hint="A page of simple shapes for trying markers and pens." />
        </SectionCard>

        <SectionCard className="span-2" icon="target" title="KDP / paperback numbers">
          <div className="coloring-fields">
            <div className="field">
              <label className="label">Paper</label>
              <select
                className="select"
                value={p.paperType}
                onChange={(e) => {
                  const preset = PAPER.find((x) => x.id === e.target.value)!;
                  void setPrint({ paperType: preset.id, paperThicknessIn: preset.thickness });
                }}
              >
                {PAPER.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            </div>
            <NumberField label="Paper thickness per page" value={p.paperThicknessIn} step={0.0001} min={0.0005} max={0.01} suffix="in" onSave={(n) => void setPrint({ paperThicknessIn: n })} hint="Used to work out the spine width." />
          </div>
          {summary && (
            <dl className="kdp-grid">
              <div><dt>Trim size</dt><dd>{summary.kdp.trimWidthIn} × {summary.kdp.trimHeightIn} in</dd></div>
              <div><dt>Interior pages</dt><dd>{summary.interior.leaves}{summary.interior.blanks > 0 ? ` (${summary.interior.blanks} blank)` : ""}</dd></div>
              <div><dt>Spine width</dt><dd>{summary.kdp.spineIn.toFixed(4)} in</dd></div>
              <div><dt>Full cover size</dt><dd>{summary.kdp.widthIn.toFixed(3)} × {summary.kdp.heightIn.toFixed(3)} in</dd></div>
              <div><dt>Pages with artwork</dt><dd>{summary.pages.withArt} of {summary.pages.total}</dd></div>
              <div><dt>Approved</dt><dd>{summary.pages.withArt - summary.pages.unapproved} of {summary.pages.withArt}</dd></div>
            </dl>
          )}
          {summary?.interior.belowMinimum && (
            <div className="gen-note warn"><Icon name="alert" size={14} /><span>Amazon KDP paperbacks need at least 24 pages; this interior has {summary.interior.leaves}.</span></div>
          )}
          <p className="faint" style={{ fontSize: 12, margin: "8px 0 0" }}>
            <Icon name="info" size={12} /> These values are configurable because Amazon changes its specifications. Before publishing, check them against KDP's current
            trim-size and cover-calculator pages. Spine text is only added when the spine is at least 0.125 in wide.
          </p>
        </SectionCard>
      </div>
    </div>
  );
}
